import React, { forwardRef, ReactNode, useId } from 'react';
import { FieldError } from 'react-hook-form';
import { ExclamationCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { classNames } from 'support/helpers/generic/generic';
import { FieldState } from 'components/Form/types';

import { InputLabel } from '../InputLabel/InputLabel';

type TextareaVariantProps =
  | { variant?: 'default'; valueDescription?: never }
  | { variant: 'large'; valueDescription?: string };

type TextareaType = TextareaVariantProps & {
  name?: string;
  label?: string;
  rows?: number;
  errors?: FieldError;
  hasErrors?: boolean;
  fieldState?: FieldState;
  wrapperClassName?: string;
  wrapperRef?: React.Ref<HTMLDivElement>;
  required?: boolean;
  value?: string;
  className?: string;
  description?: ReactNode;
} & Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, 'size'>;

function TextareaComponent(
  {
    variant = 'default',
    valueDescription,
    description,
    label,
    required,
    rows = 3,
    value,
    name,
    errors,
    hasErrors,
    fieldState,
    wrapperClassName,
    wrapperRef,
    className,
    ...otherProps
  }: TextareaType,
  ref: React.Ref<HTMLTextAreaElement>,
) {
  const textAreaId = useId();
  const isLarge = variant === 'large';
  const resolvedState: FieldState = fieldState ?? (errors || hasErrors ? 'error' : 'default');

  return (
    <div
      className={classNames(wrapperClassName, {
        'opacity-40': otherProps.disabled,
        'text-red-700 focus-within:text-inherit': resolvedState === 'error',
        'text-blue-700 focus-within:text-inherit': resolvedState === 'pending-validation',
      })}
      ref={wrapperRef}
    >
      {/* Label + Description when large */}
      {(label || description) && isLarge ? (
        <div className="mb-2 flex flex-col gap-1">
          <InputLabel label={label} variant={variant} required={required} />
          {description && <p className="text-sm text-gray-700">{description}</p>}
        </div>
      ) : (
        <InputLabel label={label} variant={variant} required={required} htmlFor={textAreaId} />
      )}

      {/* Value Description + Textarea */}
      {valueDescription && <p className="mb-1 text-sm text-gray-500">{valueDescription}</p>}

      <div className={classNames('relative rounded-md shadow-sm', !isLarge && label ? 'mt-1' : undefined)}>
        <textarea
          {...otherProps}
          ref={ref}
          name={name}
          id={textAreaId}
          className={classNames(
            'data-hj-allow shadow-sm block w-full sm:text-sm rounded-md focus-visible:outline-none box-border border-gray-300',
            {
              'bg-red-50 placeholder:text-red-700 focus-visible:ring-red-500 focus-visible:border-red-500 focus:bg-transparent':
                resolvedState === 'error',
              'bg-blue-50 placeholder:text-blue-700 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus:bg-transparent':
                resolvedState === 'pending-validation',
              'focus-visible:ring-procuros-green-500 focus-visible:border-procuros-green-500':
                resolvedState === 'default',
            },
            className,
          )}
          rows={rows}
          value={value}
        />

        {resolvedState === 'error' ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="size-5 text-red-500" aria-hidden="true" />
          </div>
        ) : resolvedState === 'pending-validation' ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <MinusCircleIcon className="size-5 text-blue-500" aria-hidden="true" />
          </div>
        ) : null}
      </div>

      {!!errors && <p className="mt-2 text-sm text-red-600">{errors.message}</p>}
      {/* when default size, show description bellow textarea */}
      {!!description && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
    </div>
  );
}

export const Textarea = forwardRef(TextareaComponent);
